import { BaseModal, Container, Group, Text, useBreakpoint } from '@melio/penny';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useSubscriptionMe } from '@melio/subscriptions-api';
import { SubscriptionContextualPaywallProps, useSubscriptionContextualPaywall } from '@melio/subscriptions-sdk';
import { useLocation, useNavigate } from 'react-router-dom';

import { ContextualPlanCard } from './components';
import { useContextualPlans } from './hooks/useContextualPlans';

const ContextualPaywallModal = ({ featureName, onDismiss }: SubscriptionContextualPaywallProps) => {
  const { hidePaywall } = useSubscriptionContextualPaywall();
  const contextualPlans = useContextualPlans({ featureName });
  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const { data: mySubscription } = useSubscriptionMe({ enabled: isPlatformSubscriptionsEnabled });
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isExtraSmallScreen } = useBreakpoint();

  const handleClose = () => {
    hidePaywall();
    onDismiss?.();
  };

  const handlePlanUpgradeClick = () => {
    hidePaywall();
    if (contextualPlans?.upsell) {
      navigate(
        { pathname: '/subscription/checkout', search: `planId=${contextualPlans.upsell.upgradePlan.id}` },
        { state: { returnUrl: pathname } }
      );
    }
  };

  const billingCycle = mySubscription?.billingCycle || 'monthly';

  if (!contextualPlans) {
    return null;
  }

  const modalPadding = isExtraSmallScreen ? 'm' : 'xxl';

  return (
    <BaseModal
      isOpen
      onClose={handleClose}
      size="medium"
      data-testid="contextual-plans-modal-activity"
      isLoading={contextualPlans.isFetching}
    >
      <Container paddingX={modalPadding} paddingY={modalPadding}>
        {contextualPlans.upsell && (
          <Group variant="vertical">
            <Text textStyle="heading2Semi">{contextualPlans.upsell.title}</Text>
            <Text textStyle="body3" color="neutral.darkest">
              {contextualPlans.upsell.description}
            </Text>
            <Group>
              <ContextualPlanCard
                isCurrentPlan
                upsellContext={contextualPlans.upsell.currentContextualPlan}
                billingCycle={billingCycle}
                onClick={handleClose}
                plan={contextualPlans.upsell.currentPlan}
              />
              <ContextualPlanCard
                upsellContext={contextualPlans.upsell.upgradeContextualPlan}
                billingCycle={billingCycle}
                onClick={handlePlanUpgradeClick}
                plan={contextualPlans.upsell.upgradePlan}
              />
            </Group>
          </Group>
        )}
      </Container>
    </BaseModal>
  );
};

export const ContextualPaywallModalActivity = () => {
  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const { paywallProps } = useSubscriptionContextualPaywall();

  if (!isPlatformSubscriptionsEnabled || !paywallProps) {
    return null;
  }

  return <ContextualPaywallModal {...paywallProps} />;
};
